import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, ExpandMore, KeyboardArrowDown, KeyboardArrowUp, Save, Sell } from '@mui/icons-material';
import api from '../../services/api';
import type { CostItem, CostStage, FactoryCosts, FactoryCostSettings, ProductCost } from '../../types/factory';
import { Kpi, PageHeader, ProductSelect, errorMessage, fmtMoney, fmtMoney2, fmtPct, fmtQty, num } from './shared';

// Los % se cargan como números enteros (95) y se guardan como fracción (0,95).
const toPct = (v: number) => String(Math.round(v * 10000) / 100);
const fromPct = (v: string) => num(v) / 100;

/** Costeo por producto: MP de la formulación + MO por etapa de máquina + parte de estructura + armado. */
const FactoryCostsPage: React.FC = () => {
  const [data, setData] = useState<FactoryCosts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ProfileForm | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.getFactoryCosts());
    } catch (e) {
      setError(errorMessage(e, 'No se pudo cargar el costeo'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async (r: ProductCost, applyPrice: boolean) => {
    const what = applyPrice
      ? `costo ${fmtMoney2(r.totalCost)} y precio ${fmtMoney2(r.suggestedPrice)}`
      : `costo ${fmtMoney2(r.totalCost)}`;
    if (!window.confirm(`¿Guardar ${what} en el producto ${r.productName}?`)) return;
    try {
      await api.applyProductCost(r.productId, applyPrice);
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo actualizar el producto'));
    }
  };

  if (!data) {
    return error ? <Alert severity="error">{error}</Alert> : <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const shareOk = data.rows.length === 0 || Math.abs(data.structureShareTotal - 1) < 0.005;

  return (
    <Box>
      <PageHeader
        title="Costeo"
        description={<>Materia prima de la formulación × merma, mano de obra por hora de máquina, la parte de estructura de cada línea y el armado. Precio propuesto = costo + utilidad + impositivo.</>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <SettingsCard settings={data.settings} onSaved={load} />

      {!shareOk && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          La estructura repartida entre los productos suma {fmtPct(data.structureShareTotal, 1)}: tendría que dar 100% para que se absorba toda.
        </Alert>
      )}

      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography fontWeight={700} sx={{ flex: 1 }}>Productos costeados</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setEditing(emptyProfile())}>Costear producto</Button>
      </Stack>
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Producto</TableCell>
                <TableCell align="right">MP</TableCell>
                <TableCell align="right">MO</TableCell>
                <TableCell align="right">Estructura</TableCell>
                <TableCell align="right">Armado</TableCell>
                <TableCell align="right">Costo</TableCell>
                <TableCell align="right">Utilidad</TableCell>
                <TableCell align="right">Precio propuesto</TableCell>
                <TableCell align="right">Precio actual</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Todavía no hay productos costeados. La materia prima sale de la formulación activa de cada producto.
                </TableCell></TableRow>
              )}
              {data.rows.map(r => {
                const diff = r.currentPrice > 0 ? r.currentPrice / r.suggestedPrice - 1 : null;
                return (
                  <React.Fragment key={r.productId}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => setOpen(open === r.productId ? null : r.productId)}>
                          {open === r.productId ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {r.productName}
                        {!r.hasRecipe && <Tooltip title="Sin formulación activa: la materia prima da 0"><Chip size="small" color="warning" label="sin formulación" sx={{ ml: 1 }} /></Tooltip>}
                      </TableCell>
                      <TableCell align="right">{fmtMoney2(r.materialCost)}</TableCell>
                      <TableCell align="right">{fmtMoney2(r.laborCost)}</TableCell>
                      <TableCell align="right">{fmtMoney2(r.structureCost)}</TableCell>
                      <TableCell align="right">{fmtMoney2(r.assemblyCostPerUnit)}</TableCell>
                      <TableCell align="right"><b>{fmtMoney2(r.totalCost)}</b></TableCell>
                      <TableCell align="right">{fmtPct(r.marginPct)}</TableCell>
                      <TableCell align="right"><b>{fmtMoney2(r.suggestedPrice)}</b></TableCell>
                      <TableCell align="right">
                        {fmtMoney2(r.currentPrice)}
                        {diff != null && Math.abs(diff) >= 0.01 && (
                          <Typography variant="caption" display="block" color={diff < 0 ? 'error.main' : 'success.main'}>
                            {diff > 0 ? '+' : ''}{fmtPct(diff)} vs propuesto
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Editar costeo"><IconButton size="small" onClick={() => setEditing(profileFrom(r))}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Guardar el costo en el producto"><IconButton size="small" onClick={() => apply(r, false)}><Save fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Guardar costo y precio propuesto en el producto"><IconButton size="small" onClick={() => apply(r, true)}><Sell fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={11} sx={{ py: 0, borderBottom: open === r.productId ? undefined : 'none' }}>
                        <Collapse in={open === r.productId} unmountOnExit>
                          <Breakdown row={r} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {editing && <ProfileDialog form={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} taken={data.rows.map(r => r.productId)} />}
    </Box>
  );
};

// ==================== Bases del mes ====================

const SettingsCard: React.FC<{ settings: FactoryCostSettings; onSaved: () => void }> = ({ settings, onSaved }) => {
  const [s, setS] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setS(settings), [settings]);

  const labor = s.laborItems.reduce((a, i) => a + (Number(i.amount) || 0), 0);
  const structure = s.structureItems.reduce((a, i) => a + (Number(i.amount) || 0), 0);
  const plantHours = s.hoursPerDay * s.daysPerWeek * s.weeksPerMonth;
  const laborHours = plantHours * s.employees;

  const save = async () => {
    setSaving(true);
    try {
      await api.saveFactoryCostSettings(s);
      setError(null);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudieron guardar las bases'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Accordion variant="outlined" defaultExpanded={settings.laborItems.length === 0} sx={{ mb: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ width: '100%' }}>
          <Typography fontWeight={700}>Bases del mes</Typography>
          <Typography variant="body2" color="text.secondary">Hora de MO: <b>{fmtMoney2(settings.laborHourCost)}</b></Typography>
          <Typography variant="body2" color="text.secondary">Hora de estructura: <b>{fmtMoney2(settings.structureHourCost)}</b></Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField size="small" type="number" label="Horas por día" value={s.hoursPerDay} onChange={e => setS({ ...s, hoursPerDay: num(e.target.value) })} />
          <TextField size="small" type="number" label="Días por semana" value={s.daysPerWeek} onChange={e => setS({ ...s, daysPerWeek: num(e.target.value) })} />
          <TextField size="small" type="number" label="Semanas por mes" value={s.weeksPerMonth} onChange={e => setS({ ...s, weeksPerMonth: num(e.target.value) })} />
          <TextField size="small" type="number" label="Empleados" value={s.employees} onChange={e => setS({ ...s, employees: Math.round(num(e.target.value)) })} />
          <TextField size="small" type="number" label="Impositivo %" value={toPct(s.taxPct)} onChange={e => setS({ ...s, taxPct: fromPct(e.target.value) })} />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <ItemsEditor title="Mano de obra del mes" hint="Sueldos, cargas sociales, honorarios de producción"
            items={s.laborItems} onChange={laborItems => setS({ ...s, laborItems })} />
          <ItemsEditor title="Estructura del mes" hint="Luz, alquiler, mantenimiento, seguros, administración, costos financieros"
            items={s.structureItems} onChange={structureItems => setS({ ...s, structureItems })} />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Kpi label="MO del mes" value={fmtMoney(labor)} hint={`${fmtQty(laborHours)} horas-hombre`} />
          <Kpi label="Hora de MO" value={fmtMoney2(laborHours > 0 ? labor / laborHours : 0)} />
          <Kpi label="Estructura del mes" value={fmtMoney(structure)} hint={`${fmtQty(plantHours)} horas de planta`} />
          <Kpi label="Hora de estructura" value={fmtMoney2(plantHours > 0 ? structure / plantHours : 0)} />
          <Button variant="contained" onClick={save} disabled={saving}>Guardar bases</Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const ItemsEditor: React.FC<{ title: string; hint: string; items: CostItem[]; onChange: (items: CostItem[]) => void }> = ({ title, hint, items, onChange }) => (
  <Box sx={{ flex: 1 }}>
    <Typography fontWeight={700}>{title}</Typography>
    <Typography variant="caption" color="text.secondary">{hint}</Typography>
    <Stack spacing={1} sx={{ mt: 1 }}>
      {items.map((it, i) => (
        <Stack key={i} direction="row" spacing={1}>
          <TextField size="small" label="Concepto" value={it.name} sx={{ flex: 1 }}
            onChange={e => onChange(items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
          <TextField size="small" type="number" label="Monto" value={it.amount || ''} sx={{ width: 150 }}
            onChange={e => onChange(items.map((x, j) => (j === i ? { ...x, amount: num(e.target.value) } : x)))} />
          <IconButton size="small" onClick={() => onChange(items.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<Add />} onClick={() => onChange([...items, { name: '', amount: 0 }])} sx={{ alignSelf: 'flex-start' }}>Agregar</Button>
    </Stack>
  </Box>
);

// ==================== Detalle ====================

const Breakdown: React.FC<{ row: ProductCost }> = ({ row }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ py: 2 }}>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2">Materia prima (formulación)</Typography>
      <Table size="small">
        <TableBody>
          {row.materials.map((m, i) => (
            <TableRow key={i}>
              <TableCell>{m.name}</TableCell>
              <TableCell align="right">{m.quantity} {m.unit || ''}</TableCell>
              <TableCell align="right">× {fmtMoney2(m.unitCost)}</TableCell>
              <TableCell align="right">{fmtMoney2(m.total)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3}>Subtotal · aprovechamiento {fmtPct(row.yieldPct)} (merma {fmtPct(1 - row.yieldPct)})</TableCell>
            <TableCell align="right"><b>{fmtMoney2(row.materialCost)}</b></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2">Mano de obra y estructura</Typography>
      <Table size="small">
        <TableBody>
          {row.stages.map((s, i) => (
            <TableRow key={i}>
              <TableCell>{s.name}</TableCell>
              <TableCell align="right">{fmtQty(s.unitsPerHour)} u/h × {fmtPct(s.utilizationPct)}{s.laborFactor !== 1 ? ` · factor ${s.laborFactor}` : ''}</TableCell>
              <TableCell align="right">{fmtMoney2(s.laborCostPerUnit)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={2}>Estructura · {fmtPct(row.structureSharePct, 1)} de la planta</TableCell>
            <TableCell align="right">{fmtMoney2(row.structureCost)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}>Utilidad {fmtPct(row.marginPct)} + impositivo {fmtPct(row.taxPct)}{row.taxOverridden ? ' (propio)' : ''}</TableCell>
            <TableCell align="right">{fmtMoney2(row.marginAmount + row.taxAmount)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}>Si la utilidad fuera sobre el precio de venta</TableCell>
            <TableCell align="right">{fmtMoney2(row.priceOnSaleMargin)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {row.notes && <Typography variant="caption" color="text.secondary">{row.notes}</Typography>}
    </Box>
  </Stack>
);

// ==================== Perfil de costeo ====================

type ProfileForm = {
  productId: number | null;
  isNew: boolean;
  stages: { name: string; unitsPerHour: string; utilization: string; laborFactor: string }[];
  share: string;
  yield: string;
  assembly: string;
  margin: string;
  /** Vacío = el impositivo de las bases del mes. */
  tax: string;
  notes: string;
};

const emptyProfile = (): ProfileForm => ({
  productId: null, isNew: true, stages: [{ name: 'Inyección', unitsPerHour: '', utilization: '95', laborFactor: '1' }],
  share: '0', yield: '95', assembly: '0', margin: '30', tax: '', notes: '',
});

const profileFrom = (r: ProductCost): ProfileForm => ({
  productId: r.productId,
  isNew: false,
  stages: r.stages.map(s => ({ name: s.name, unitsPerHour: String(s.unitsPerHour), utilization: toPct(s.utilizationPct), laborFactor: String(s.laborFactor ?? 1) })),
  share: toPct(r.structureSharePct),
  yield: toPct(r.yieldPct),
  assembly: String(r.assemblyCostPerUnit),
  margin: toPct(r.marginPct),
  tax: r.taxOverridden ? toPct(r.taxPct) : '',
  notes: r.notes || '',
});

const ProfileDialog: React.FC<{ form: ProfileForm; taken: number[]; onClose: () => void; onSaved: () => void }> = ({ form: initial, taken, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setStage = (i: number, patch: Partial<ProfileForm['stages'][number]>) =>
    setForm({ ...form, stages: form.stages.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  const save = async () => {
    if (!form.productId) { setError('Elegí el producto'); return; }
    if (form.isNew && taken.includes(form.productId)) { setError('Ese producto ya está costeado: editalo desde la tabla'); return; }
    setSaving(true);
    try {
      const stages: CostStage[] = form.stages.map(s => ({
        name: s.name, unitsPerHour: num(s.unitsPerHour), utilizationPct: fromPct(s.utilization), laborFactor: s.laborFactor === '' ? 1 : num(s.laborFactor),
      }));
      await api.saveProductCostProfile(form.productId, {
        stages,
        structureSharePct: fromPct(form.share),
        yieldPct: fromPct(form.yield),
        assemblyCostPerUnit: num(form.assembly),
        marginPct: fromPct(form.margin),
        taxPct: form.tax === '' ? null : fromPct(form.tax),
        notes: form.notes || null,
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar el costeo'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.productId || !window.confirm('¿Sacar este producto del costeo? El producto no se borra.')) return;
    await api.deleteProductCostProfile(form.productId);
    onSaved();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{form.isNew ? 'Costear producto' : 'Editar costeo'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {form.isNew
            ? <ProductSelect value={form.productId} onChange={id => setForm({ ...form, productId: id })} required />
            : null}
          <Box>
            <Typography variant="subtitle2">Etapas de máquina</Typography>
            <Typography variant="caption" color="text.secondary">
              Cada etapa suma MO: hora de MO / (unidades por hora × utilización) × factor. Ej. tapón: inyección 5200 u/h y oring 7600 u/h. Factor 0,5 = un operario atiende dos máquinas. La primera etapa reparte la estructura.
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {form.stages.map((s, i) => (
                <Stack key={i} direction="row" spacing={1}>
                  <TextField size="small" label="Etapa" value={s.name} onChange={e => setStage(i, { name: e.target.value })} sx={{ flex: 1 }} />
                  <TextField size="small" type="number" label="Unidades/hora" value={s.unitsPerHour} onChange={e => setStage(i, { unitsPerHour: e.target.value })} sx={{ width: 130 }} />
                  <TextField size="small" type="number" label="Utilización %" value={s.utilization} onChange={e => setStage(i, { utilization: e.target.value })} sx={{ width: 120 }} />
                  <TextField size="small" type="number" label="Factor MO" value={s.laborFactor} onChange={e => setStage(i, { laborFactor: e.target.value })} sx={{ width: 100 }} />
                  <IconButton size="small" disabled={form.stages.length === 1} onClick={() => setForm({ ...form, stages: form.stages.filter((_, j) => j !== i) })}><Delete fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<Add />} sx={{ alignSelf: 'flex-start' }}
                onClick={() => setForm({ ...form, stages: [...form.stages, { name: '', unitsPerHour: '', utilization: '95', laborFactor: '1' }] })}>Agregar etapa</Button>
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            <TextField size="small" type="number" label="Estructura de la planta %" value={form.share} onChange={e => setForm({ ...form, share: e.target.value })}
              helperText="Qué parte de la estructura absorbe esta línea" />
            <TextField size="small" type="number" label="Aprovechamiento %" value={form.yield} onChange={e => setForm({ ...form, yield: e.target.value })}
              helperText="95 = 5% de merma" />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField size="small" type="number" label="Armado por unidad $" value={form.assembly} onChange={e => setForm({ ...form, assembly: e.target.value })}
              helperText="Ej. MO calle: precio bolsa / unidades" />
            <TextField size="small" type="number" label="Utilidad %" value={form.margin} onChange={e => setForm({ ...form, margin: e.target.value })} helperText=" " />
            <TextField size="small" type="number" label="Impositivo propio %" value={form.tax} onChange={e => setForm({ ...form, tax: e.target.value })}
              helperText="Vacío = el de las bases" />
          </Stack>
          <TextField size="small" label="Notas" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} multiline />
        </Stack>
      </DialogContent>
      <DialogActions>
        {!form.isNew && <Button color="error" onClick={remove} sx={{ mr: 'auto' }}>Sacar del costeo</Button>}
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FactoryCostsPage;
